import type { LocationSelectorOption } from './buildBrewhouseLocationSelectorOptions';

const GROUP_SCOPE_PREFIX = 'group:';
const LOCATION_SCOPE_PREFIX = 'location:';

export type BrewhouseScopeKind = 'group' | 'location';

export interface ParsedBrewhouseScope {
  kind: BrewhouseScopeKind;
  id: string;
}

const ACTIVE_SCOPE_STORAGE_PREFIX = 'brewhouse.activeScope';

export function encodeBrewhouseScopeValue(kind: BrewhouseScopeKind, id: string): string {
  const normalizedId = typeof id === 'string' ? id.trim() : '';
  if (!normalizedId) {
    return '';
  }
  return kind === 'group'
    ? `${GROUP_SCOPE_PREFIX}${normalizedId}`
    : `${LOCATION_SCOPE_PREFIX}${normalizedId}`;
}

export function parseBrewhouseScopeValue(value: string): ParsedBrewhouseScope | null {
  const normalized = typeof value === 'string' ? value.trim() : '';
  if (!normalized) {
    return null;
  }
  if (normalized.startsWith(GROUP_SCOPE_PREFIX)) {
    const groupId = normalized.slice(GROUP_SCOPE_PREFIX.length).trim();
    return groupId.length > 0 ? { kind: 'group', id: groupId } : null;
  }
  if (normalized.startsWith(LOCATION_SCOPE_PREFIX)) {
    const breweryId = normalized.slice(LOCATION_SCOPE_PREFIX.length).trim();
    return breweryId.length > 0 ? { kind: 'location', id: breweryId } : null;
  }
  return null;
}

export function brewhouseActiveScopeStorageKey(userId: string): string {
  const normalizedUserId = typeof userId === 'string' ? userId.trim() : '';
  if (!normalizedUserId) {
    return '';
  }
  return `${ACTIVE_SCOPE_STORAGE_PREFIX}.${normalizedUserId}`;
}

export function readStoredBrewhouseActiveScope(userId: string): string | null {
  const storageKey = brewhouseActiveScopeStorageKey(userId);
  if (!storageKey) {
    return null;
  }
  try {
    const stored = window.localStorage.getItem(storageKey);
    if (typeof stored !== 'string') {
      return null;
    }
    const trimmed = stored.trim();
    return trimmed.length > 0 ? trimmed : null;
  } catch {
    return null;
  }
}

export function writeStoredBrewhouseActiveScope(userId: string, scopeValue: string): void {
  const storageKey = brewhouseActiveScopeStorageKey(userId);
  const normalizedValue = typeof scopeValue === 'string' ? scopeValue.trim() : '';
  if (!storageKey || !normalizedValue) {
    return;
  }
  try {
    window.localStorage.setItem(storageKey, normalizedValue);
  } catch {
    // ignore quota / private mode
  }
}

/**
 * Resolves a stored or requested scope against the current selector options.
 */
export function resolveValidBrewhouseActiveScope(
  scopeValue: string | null | undefined,
  options: LocationSelectorOption[],
  fallbackBreweryId: string | null,
): string {
  const optionValues = new Set(options.map((option) => option.value));
  const normalizedScope =
    typeof scopeValue === 'string' ? scopeValue.trim() : '';

  if (normalizedScope && optionValues.has(normalizedScope)) {
    return normalizedScope;
  }

  const fallbackLocationScope = fallbackBreweryId
    ? encodeBrewhouseScopeValue('location', fallbackBreweryId)
    : '';
  if (fallbackLocationScope && optionValues.has(fallbackLocationScope)) {
    return fallbackLocationScope;
  }

  return options[0]?.value ?? '';
}

export function findBrewhouseScopeOption(
  options: LocationSelectorOption[],
  scopeValue: string,
): LocationSelectorOption | null {
  const normalizedScope = typeof scopeValue === 'string' ? scopeValue.trim() : '';
  if (!normalizedScope) {
    return null;
  }
  return options.find((option) => option.value === normalizedScope) ?? null;
}
